import { getAuthPayload, hasPermission, isContractorUser } from "Common/permissions";
import { hasPlatformPermission } from "Common/platformAccess";

export type RouteAccessRule = {
  allowedUserTypes: string[];
  requiredPermission?: string | string[];
  requiredPlatformPermission?: string | string[];
};

const ALL_AUTHENTICATED_ROLES = [
  "admin",
  "contractor",
  "homeowner",
  "real_estate_agent",
  "broker",
  "subcontractor",
  "inspector",
];

const CONTRACT_ROLES = ["admin", "contractor", "homeowner", "subcontractor", "inspector"];
const CHAT_ROLES = ["admin", "contractor", "homeowner", "real_estate_agent", "broker"];
const AGENT_ROLES = ["real_estate_agent", "broker"];

const allow = (
  allowedUserTypes: string[],
  options: Omit<RouteAccessRule, "allowedUserTypes"> = {}
): RouteAccessRule => ({ allowedUserTypes, ...options });

/**
 * Route authorization is deliberately fail-closed. Every authenticated route
 * must be registered here before it can be opened directly by URL.
 *
 * Normal company permissions are enforced for contractor accounts, matching
 * the sidebar contract. Platform permissions are enforced for admin accounts.
 */
export const ROUTE_ACCESS_RULES: Record<string, RouteAccessRule> = {
  "/": allow(ALL_AUTHENTICATED_ROLES),
  "*": allow(ALL_AUTHENTICATED_ROLES),
  "/access-denied": allow(ALL_AUTHENTICATED_ROLES),
  "/dashboard": allow(ALL_AUTHENTICATED_ROLES, {
    requiredPermission: ["View Dashboard", "VIEW_DASHBOARD"],
    requiredPlatformPermission: "PLATFORM_DASHBOARD_VIEW",
  }),

  // Shared account and utility pages
  "/pages-profile-settings": allow([
    "contractor",
    "homeowner",
    "real_estate_agent",
    "broker",
    "subcontractor",
    "inspector",
  ]),
  "/profile": allow(ALL_AUTHENTICATED_ROLES),
  "/pages-profile": allow(ALL_AUTHENTICATED_ROLES),
  "/pages-credentials": allow(ALL_AUTHENTICATED_ROLES),
  "/pages-term-conditions": allow(ALL_AUTHENTICATED_ROLES),
  "/pages-faqs": allow(ALL_AUTHENTICATED_ROLES),
  "/pages-pricing": allow(ALL_AUTHENTICATED_ROLES),
  "/pages-timeline": allow(ALL_AUTHENTICATED_ROLES),
  "/logan-ai": allow(ALL_AUTHENTICATED_ROLES),

  // Admin operations
  "/billing/cards-expiring-soon": allow(["admin"], { requiredPlatformPermission: "BILLING_VIEW" }),
  "/billing/payment-alerts": allow(["admin"], { requiredPlatformPermission: "BILLING_VIEW" }),
  "/twilio-notifications": allow(["admin"], { requiredPlatformPermission: "NOTIFICATION_SEND" }),
  "/free-estimates": allow(["admin"], { requiredPlatformPermission: "COMPANY_VIEW" }),
  "/insurance-contacts": allow(["admin"], { requiredPlatformPermission: "COMPLIANCE_VIEW" }),
  "/pages-all-contractors": allow(["admin"], { requiredPlatformPermission: "COMPANY_VIEW" }),
  "/pages-all-homeowners": allow(["admin"], { requiredPlatformPermission: "USER_VIEW" }),
  "/pages-all-real-estate-agents": allow(["admin"], { requiredPlatformPermission: "USER_VIEW" }),
  "/pages-all-leads": allow(["admin"], { requiredPlatformPermission: "LEAD_VIEW" }),
  "/escrow-requests": allow(["admin"], { requiredPlatformPermission: "BILLING_VIEW" }),
  "/catogery-master": allow(["admin"], { requiredPlatformPermission: "CONTENT_MANAGE" }),
  "/sub-catogery-master": allow(["admin"], { requiredPlatformPermission: "CONTENT_MANAGE" }),
  "/coupan-dashboard": allow(["admin"], { requiredPlatformPermission: "COUPON_MANAGE" }),
  "/version-controle": allow(["admin"], { requiredPlatformPermission: "APP_VERSION_MANAGE" }),
  "/push-notification": allow(["admin"], { requiredPlatformPermission: "NOTIFICATION_SEND" }),
  "/mls-cache-control": allow(["admin"], { requiredPlatformPermission: "CONTENT_MANAGE" }),
  "/property-leads": allow(["admin"], { requiredPlatformPermission: "LEAD_VIEW" }),
  "/pages-all-admins": allow(["admin"], { requiredPlatformPermission: "PLATFORM_STAFF_VIEW" }),
  "/platform-access": allow(["admin"], { requiredPlatformPermission: "PLATFORM_STAFF_VIEW" }),
  "/pages-single-homeowner": allow(["admin"], { requiredPlatformPermission: "USER_VIEW" }),
  "/pages-single-contractors": allow(["admin"], { requiredPlatformPermission: "COMPANY_VIEW" }),
  "/pages-all-forms": allow(["admin"], { requiredPlatformPermission: "LEAD_VIEW" }),
  "/pages-all-subscribers": allow(["admin"], { requiredPlatformPermission: "USER_VIEW" }),
  "/logan-ai-admin": allow(["admin"]),
  "/apps-tickets-overview": allow(["admin"]),
  "/pages-contacts": allow(["admin"]),

  // Contracts and contract-adjacent pages
  "/get-contracts": allow(CONTRACT_ROLES, { requiredPermission: "VIEW_CONTRACT" }),
  "/create-contract": allow(CONTRACT_ROLES, {
    requiredPermission: ["VIEW_CONTRACT", "CREATE_CONTRACT"],
  }),
  "/view-sub-contracts": allow(["admin", "contractor", "subcontractor"], {
    requiredPermission: ["VIEW_SUBCONTRACT", "MANAGE_SUBCONTRACT"],
  }),
  "/live-contracts": allow(CONTRACT_ROLES, { requiredPermission: "VIEW_CONTRACT" }),
  "/live-contract-details": allow(CONTRACT_ROLES, { requiredPermission: "VIEW_CONTRACT" }),
  "/contracts-list": allow(["admin", "contractor"], { requiredPermission: "VIEW_CONTRACT" }),
  "/apps-contracts-overview": allow(["admin", "contractor"], { requiredPermission: "VIEW_CONTRACT" }),
  "/apps-contracts-create": allow(["admin", "contractor"], { requiredPermission: "CREATE_CONTRACT" }),
  "/create-by-ai": allow(["contractor"], { requiredPermission: "CREATE_CONTRACT" }),
  "/contract-tutorial-and-plans": allow(["contractor"]),
  "/create-sworn-contracts": allow(["admin", "contractor"], { requiredPermission: "CREATE_CONTRACT" }),
  "/create-sworn-homeowner": allow(["admin", "contractor"], { requiredPermission: "CREATE_CONTRACT" }),
  "/mechanics-lien-intakes": allow(["admin", "contractor", "subcontractor"]),

  // Contractor workspace
  "/team_management": allow(["contractor"], {
    requiredPermission: ["VIEW_COMPANY_STAFF", "MANAGE_COMPANY_STAFF", "MANAGE_TEAM"],
  }),
  "/invoices-list": allow(["contractor"], { requiredPermission: "MANAGE_BILLING" }),
  "/apps-invoices-overview": allow(["contractor"], { requiredPermission: "MANAGE_BILLING" }),
  "/apps-invoices-create": allow(["contractor"], { requiredPermission: "MANAGE_BILLING" }),
  "/credit_card_precessing": allow(["contractor"]),
  "/property_insurance": allow(["contractor"]),
  "/life_health_insurance": allow(["contractor"]),
  "/investments": allow(["contractor"]),
  "/marketing": allow(["contractor"]),
  "/create-your-team": allow(["contractor"]),
  "/project-requested": allow(["contractor"], { requiredPermission: "VIEW_JOBS" }),
  "/project-ongoing-leads": allow(["contractor"], { requiredPermission: "VIEW_JOBS" }),
  "/leadHistory": allow(["contractor"], { requiredPermission: "VIEW_JOBS" }),
  "/pages-user-overview": allow(["contractor"], { requiredPermission: "VIEW_JOBS" }),

  // Merchant and billing pages
  "/merchant/onboarding": allow(["admin", "contractor"]),
  "/merchant/business-info": allow(["admin", "contractor"]),
  "/merchant/owners": allow(["admin", "contractor"]),
  "/merchant/kyc": allow(["admin", "contractor"]),
  "/merchant/status": allow(["admin", "contractor"]),
  "/merchant/payment-setup": allow(["admin", "contractor"]),
  "/merchant/payments": allow(["admin", "contractor"]),
  "/merchant/payouts": allow(["admin", "contractor"]),
  "/wallet": allow(["admin", "contractor"], { requiredPlatformPermission: "BILLING_VIEW" }),
  "/pages-all-transactions": allow(["admin", "contractor"], {
    requiredPermission: "MANAGE_BILLING",
    requiredPlatformPermission: "BILLING_VIEW",
  }),
  "/kyc-status-page": allow(["contractor", "homeowner"]),
  "/kyc-form-page": allow(["contractor", "homeowner"]),

  // Homeowner workspace
  "/pages-projects": allow(["homeowner", "contractor"], { requiredPermission: "VIEW_JOBS" }),
  "/recent-contractors": allow(["homeowner"]),
  "/recent-contractors/:id": allow(["homeowner"]),
  "/suggested-contractors": allow(["homeowner"]),
  "/recommended-properties": allow(["homeowner"]),
  "/property-manager": allow(["homeowner"]),
  "/pages-contractors": allow(["homeowner", "real_estate_agent", "broker"]),
  "/pages-contracor-profile": allow(["homeowner", "real_estate_agent", "broker"]),

  // Real-estate agent and broker workspace
  "/agent-property-leads": allow(AGENT_ROLES),
  "/agent-my-property-leads": allow(AGENT_ROLES),
  "/agent-property-recommendations": allow(AGENT_ROLES),
  "/apps-real-estate-agent-homeowner": allow(AGENT_ROLES),
  "/apps-real-estate-explore-contractor": allow(AGENT_ROLES),
  "/apps-real-estate-agent-grid-view": allow(["admin", ...AGENT_ROLES]),
  "/apps-real-estate-grid": allow(["admin", ...AGENT_ROLES]),
  "/apps-real-estate-property-overview": allow(["admin", ...AGENT_ROLES]),
  "/apps-real-estate-add-properties": allow(AGENT_ROLES),

  // Leads and project details shared by their participants
  "/project-details/:id": allow(["admin", "contractor", "homeowner", ...AGENT_ROLES]),
  "/project-overview": allow(["admin", "contractor", "homeowner"]),

  // Communications and support
  "/apps-chat": allow(CHAT_ROLES, { requiredPermission: "CHAT_ACCESS" }),
  "/messages": allow(CHAT_ROLES, { requiredPermission: "CHAT_ACCESS" }),
  "/chat-room": allow(CHAT_ROLES, { requiredPermission: "CHAT_ACCESS" }),
  "/apps-tickets-list": allow(["admin", "contractor", "homeowner"]),
};

export function normalizeUserType(userType?: string) {
  const normalized = String(userType || "").trim().toLowerCase();
  if (normalized === "testcontractor") return "contractor";
  if (normalized === "real_esate_agent") return "real_estate_agent";
  return normalized;
}

export function canAccessRoute(rule?: RouteAccessRule) {
  if (!rule) return false;

  const auth = getAuthPayload();
  const userType = normalizeUserType(auth.userType);
  const allowedUserTypes = rule.allowedUserTypes.map(normalizeUserType);

  if (!userType || !allowedUserTypes.includes(userType)) return false;

  if (userType === "admin" && !hasPlatformPermission(rule.requiredPlatformPermission)) {
    return false;
  }

  if (isContractorUser(userType) && !hasPermission(rule.requiredPermission)) {
    return false;
  }

  return true;
}

export function getRouteAccessRule(path: string) {
  return ROUTE_ACCESS_RULES[path];
}
